import * as React from "react";
import { Navigate } from "react-router-dom";
import { toast } from "@eduviaos/ui-kit";

import { useEnabledModules } from "@/hooks/useEnabledModules";
import {
  getVerticalProfile,
  type VerticalConceptFlags,
  type VerticalProfile,
} from "@/lib/verticalProfile";

/**
 * Phase SC-4e — route-level guard for vertical concept flags.
 *
 * Mirrors `RouteModuleGate`: the sidebar hides the link, but deep links
 * still arrive. When a concept is false for the tenant's vertical we
 * bounce to `/dashboard` with a toast instead of rendering school-shaped UI.
 */
export interface RouteVerticalConceptGateProps {
  concept: keyof VerticalConceptFlags;
  children: React.ReactNode;
}

function conceptEnabled(profile: VerticalProfile, concept: keyof VerticalConceptFlags): boolean {
  return profile.has[concept];
}

export function RouteVerticalConceptGate({ concept, children }: RouteVerticalConceptGateProps) {
  const { vertical, isLoading, isError } = useEnabledModules();

  if (isLoading) {
    return null;
  }

  if (!isError && vertical) {
    const profile = getVerticalProfile(vertical);
    if (conceptEnabled(profile, concept)) {
      return <>{children}</>;
    }
  }

  return <ConceptGateRedirect />;
}

function ConceptGateRedirect() {
  React.useEffect(() => {
    toast.error("This area isn't available for your institution type.", {
      description: "The feature you're looking for applies to a different kind of institution.",
      duration: 4000,
    });
  }, []);
  return <Navigate to="/dashboard" replace />;
}
